import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { getCardMaster } from "@/lib/card-master.functions";
import { PriceHistoryChart } from "@/components/price-history-chart";
import { PopulationReport } from "@/components/population-report";
import { RouteErrorBoundary, RouteNotFoundBoundary } from "@/components/route-boundaries";
import { socialImageMeta } from "@/lib/seo";

export const Route = createFileRoute("/cards/$masterId")({
  head: ({ params }) => {
    const url = `https://bidnovaauctions.uk/cards/${params.masterId}`;
    const title = "Card catalog — BidNova Auctions";
    const description =
      "Population, price history and live listings for this graded card on BidNova Auctions.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        ...socialImageMeta(),
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: CardMasterPage,
  errorComponent: RouteErrorBoundary,
  notFoundComponent: RouteNotFoundBoundary,
});

function CardMasterPage() {
  const { masterId } = Route.useParams();
  const load = useServerFn(getCardMaster);
  const { data, isLoading } = useQuery({
    queryKey: ["card-master", masterId],
    queryFn: () => load({ data: { id: masterId } }),
    staleTime: 60 * 60_000,
  });

  if (isLoading) return <div className="mx-auto max-w-4xl px-4 py-12 text-muted-foreground">Loading…</div>;
  if (!data?.master) return <div className="mx-auto max-w-4xl px-4 py-12">Card not found.</div>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const m: any = data.master;
  return (
    <div className="mx-auto max-w-4xl px-4 py-12 space-y-8">
      <header>
        <p className="text-xs uppercase tracking-widest text-gold">Card catalog</p>
        <h1 className="mt-2 font-display text-4xl">{m.name}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {m.number ? `#${m.number} · ` : ""}{m.rarity ?? ""}{m.release_year ? ` · ${m.release_year}` : ""}
        </p>
      </header>

      <section>
        <h2 className="font-display text-2xl mb-3">Price history (180 days)</h2>
        <PriceHistoryChart cardMasterId={masterId} />
      </section>

      <section>
        <h2 className="font-display text-2xl mb-3">Population report</h2>
        <PopulationReport cardMasterId={masterId} />
      </section>
    </div>
  );
}
